import { VariantDefault, VariantSimple, VariantStatus } from './subcomponents';
import { APP_SCORING_MAX_SCORE, APP_STYLE_CONFIG } from '@/constants/config';
import { ScorecardProps } from './types';
import { useScorecardFeature } from './features';
import React from 'react';

/**
 * A scorecard component that displays practice scores with optional skill level
 * indicators and action buttons.
 */
export const Scorecard: React.FC<ScorecardProps> = ({
  actions,
  actionsLayout = 'horizontal',
  className,
  description,
  isLoading = false,
  isPrinting = false,
  level,
  maxScore = APP_SCORING_MAX_SCORE,
  onShareWithManager,
  runtimeRatingCutoffs,
  score,
  scoreCardId,
  scorePercentage,
  sessionId,
  sharedWithManager,
  showSkillScale = true,
  simulationType,
  size = 'standard',
  skillLevelScaleClassName,
  title,
  useAnimatedNumbers = APP_STYLE_CONFIG.useAnimatedNumbers,
  userId,
  variant = 'default',
}) => {
  // ================================
  // Feature Hook
  // ================================
  const {
    ariaScoreLabel,
    handleShareWithManager,
    isSimpleVariant,
    isStatusVariant,
    scoreSize,
    scoreValue,
    skillLevel,
  } = useScorecardFeature({
    level,
    maxScore,
    onShareWithManager,
    runtimeRatingCutoffs,
    score,
    scoreCardId,
    scorePercentage,
    size,
    variant,
  });

  // ================================
  // Render Logic
  // ================================

  // If the variant is 'violation', 'pass', or 'fail', we show a specific
  // scorecard without skill level or description
  if (isStatusVariant) {
    const statusVariant = variant as 'violation' | 'pass' | 'fail';

    return (
      <VariantStatus
        className={className}
        isLoading={isLoading}
        score={scoreValue}
        size={size}
        variant={statusVariant}
      />
    );
  }

  // If the variant is 'simple', we show a very minimal scorecard with
  // just the score and a label
  if (isSimpleVariant) {
    return (
      <VariantSimple
        ariaScoreLabel={ariaScoreLabel}
        className={className}
        isLoading={isLoading}
        maxScore={maxScore}
        score={scoreValue}
        size={size}
      />
    );
  }

  // For all other variants, we show the default scorecard with skill level and
  // description as applicable. The specific styling of the default variant is determined by 
  // the `variant` prop, which can be 'default', 'highlighted', or 'subtle'.
  const defaultVariant = variant as 'default' | 'highlighted' | 'subtle';

  // Return the default variant, passing down all necessary props and the computed values from the
  // feature hook. The default variant will handle the presentation logic based on the `variant`
  // prop to apply different styles as needed.
  return (
    <VariantDefault
      actions={actions}
      actionsLayout={actionsLayout}
      className={className}
      description={description}
      handleShareWithManager={handleShareWithManager}
      isLoading={isLoading}
      isPrinting={isPrinting}
      maxScore={maxScore}
      score={scoreValue}
      scoreCardId={scoreCardId}
      scoreSize={scoreSize}
      runtimeRatingCutoffs={runtimeRatingCutoffs}
      sessionId={sessionId}
      sharedWithManager={sharedWithManager}
      showSkillScale={showSkillScale}
      simulationType={simulationType}
      size={size}
      skillLevel={skillLevel}
      skillLevelScaleClassName={skillLevelScaleClassName}
      title={title}
      useAnimatedNumbers={useAnimatedNumbers}
      userId={userId}
    />
  );
};

Scorecard.displayName = 'Scorecard';

export default Scorecard;
